import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface TrialBannerProps {
  trial: {
    active: boolean;
    used: boolean;
    expired: boolean;
    expiresAt: string | null;
  };
}

interface Countdown {
  hours: number;
  minutes: number;
  seconds: number;
}

function useCountdown(expiresAt: string | null): Countdown | null {
  const [timeLeft, setTimeLeft] = useState<Countdown | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setTimeLeft(null);
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const expires = new Date(expiresAt).getTime();
      const diff = Math.max(0, expires - now);

      if (diff === 0) {
        setTimeLeft(null);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return timeLeft;
}

export function TrialBanner({ trial }: TrialBannerProps) {
  const navigate = useNavigate();
  const countdown = useCountdown(trial.expiresAt);

  if (trial.expired) {
    return null;
  }

  if (trial.used) {
    return (
      <div className="glass-panel border-emerald-100 bg-gradient-to-r from-emerald-50/60 to-teal-50/60">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="ghost-chip text-emerald-600">Free product live</span>
            <h3 className="mt-3 text-xl font-semibold text-emerald-900">
              Your free AR product is live. Ready to roll AR out to more products?
            </h3>
            <p className="mt-1 text-sm text-emerald-700">
              Choose a plan to keep generating and unlock higher-quality output for your most important products.
            </p>
          </div>
          <div className="flex flex-col gap-2 md:items-end">
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="w-full rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200 transition hover:opacity-90 md:w-auto"
            >
              Choose a plan
            </button>
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="w-full rounded-2xl border border-emerald-200 bg-white/80 px-5 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50 md:w-auto"
            >
              Compare Launch vs Growth
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (trial.active && trial.expiresAt) {
    const hoursLeft = countdown ? countdown.hours : 0;
    const minutesLeft = countdown ? countdown.minutes : 0;

    return (
      <div className="glass-panel border-emerald-100 bg-gradient-to-r from-emerald-50 to-white">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="ghost-chip text-emerald-600">Free trial</span>
            <h3 className="mt-3 text-xl font-semibold text-emerald-900">
              Your first AR-ready product is free — premium modes free for now
            </h3>
            <p className="mt-1 text-sm text-emerald-700">
              Use one product photo, generate your first 3D model, and see it live on your store. The free product has no time limit; the countdown is how long premium capture &amp; render stay free on it.
            </p>
          </div>
          <div className="flex flex-col gap-2 md:items-end">
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="w-full rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200 transition hover:opacity-90 md:w-auto"
            >
              Generate my free product
            </button>
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="w-full rounded-2xl border border-emerald-200 bg-white/80 px-5 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50 md:w-auto"
            >
              See plans
            </button>
            <span className="text-xs text-emerald-600">
              {hoursLeft}h {minutesLeft}m left
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
